'use client';

import { Stack, Typography } from '@mui/material';
import React, { useState } from 'react';

import { members } from '@/assets/members';

import { TeamCard } from './TeamCard';
import { TeamCardList } from './TeamCardList';

export interface MemberItem {
  id: number;
  name: string;
  image: string;
  description: string;
  socials?: {
    type: SocialType;
    url: string;
  }[];
}

type SocialType = 'linkedin' | 'telegram';

interface TechAndProductMember {
  id: number;
  name: string;
  role: string;
  image: string;
  socials: {
    type: SocialType;
    url: string;
  }[];
}

const MEMBERS: MemberItem[] = [
  {
    id: 1,
    name: 'Jason "BitBender" Brink',
    description:
      'Jason "BitBender" Brink is a pioneering figure in blockchain technology, known for his significant contributions to digital currencies and applications. Beginning his blockchain journey in 2013, he quickly gained recognition by winning the 2014 Bill and Melinda Gates Foundation / Global Development Network Prize for "AidDollar," a blockchain concept to improve foreign aid delivery.',
    image: members.member_2,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/bitbender/' },
      { type: 'telegram', url: 'https://t.me/JasonBitBenderBrink' },
    ],
  },
  {
    id: 2,
    name: 'Jeffrey Espejo',
    description:
      'Jeff brings his leadership experience as the former VP of Blockchain and Head of Exchange Relations at Gala to the forefront of our team. His proven track record in technical operations, and solid partnerships ensures a secure foundation for innovative projects that build with LFG.',
    image: members.member_1,
    socials: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/jeffreyespejo/' }],
  },
  {
    id: 3,
    name: 'Levi Hansen',
    description:
      'Levi has over a decade of experience in the blockchain and web3 industries, with a deep understanding of these rapidly evolving fields. For the past 3 years, he has built strong foundations as the Director of Customer Support and as a Product Manager in game development, all while focusing on strong community engagement.',
    image: members.member_3,
    socials: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/levi-hansen-10387619a/' }],
  },
  {
    id: 4,
    name: 'Dillon Overton',
    description:
      'With a strong background in developing online communities over 6 years, Dillon prioritizes integrity, security and communication. He is a community-first advocate the power of blockchain technology to support positive change and accountability globally.',
    image: members.member_4,
    socials: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/dillon-overton-24516910b/' }],
  },
  {
    id: 5,
    name: 'Haley Kim',
    description:
      'Swiss-degreed, globally-minded (6 countries!), Haley brings a decade of blockchain expertise to every project. Her 4 years at Gala fueled Asian (esp.South Korea) community & business growth. Now, she champions vibrant, inclusive digital communities. leveraging her deep background and passion for the future of blockchain.',
    image: members.member_5,
    socials: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/haleykim1010/' }],
  },
  {
    id: 6,
    name: 'Anita Nguyen',
    description:
      'Anita Ho (ANeeda) brings over 6 years of experience building and operating within Web3 ecosystems at the intersection of innovation and adoption. Her work focuses on bringing clarity to complexity, establishing structure in high-growth environments, and strengthening the communities that drive sustainable scale. Observant, adaptive, and precise, she thrives in early-stage landscapes where vision must translate into operational reality and advances initiatives with discipline and precision, ensuring innovation scales without compromising its foundation.',
    image: members.member_6,
    socials: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/anita-nguyen-777a75352/' }],
  },
];

const SOCIAL_ICON_MAP: Record<SocialType, React.ReactNode> = {
  linkedin: (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M4.98292 2.82104C4.69559 2.82104 4.41107 2.87764 4.14561 2.9876C3.88015 3.09755 3.63895 3.25872 3.43577 3.4619C3.2326 3.66507 3.07143 3.90627 2.96147 4.17173C2.85152 4.43719 2.79492 4.72171 2.79492 5.00904C2.79492 5.29638 2.85152 5.5809 2.96147 5.84636C3.07143 6.11182 3.2326 6.35302 3.43577 6.55619C3.63895 6.75937 3.88015 6.92054 4.14561 7.03049C4.41107 7.14045 4.69559 7.19704 4.98292 7.19704C5.27025 7.19704 5.55477 7.14045 5.82023 7.03049C6.08569 6.92054 6.3269 6.75937 6.53007 6.55619C6.73325 6.35302 6.89441 6.11182 7.00437 5.84636C7.11433 5.5809 7.17092 5.29638 7.17092 5.00904C7.17092 4.72171 7.11433 4.43719 7.00437 4.17173C6.89441 3.90627 6.73325 3.66507 6.53007 3.4619C6.3269 3.25872 6.08569 3.09755 5.82023 2.9876C5.55477 2.87764 5.27025 2.82104 4.98292 2.82104ZM9.23692 8.85504V20.994H13.0059V14.991C13.0059 13.407 13.3039 11.873 15.2679 11.873C17.2049 11.873 17.2289 13.684 17.2289 15.091V20.995H20.9999V14.338C20.9999 11.068 20.2959 8.55505 16.4739 8.55505C14.6389 8.55505 13.4089 9.56205 12.9059 10.515H12.8549V8.85504H9.23692ZM3.09492 8.85504H6.86992V20.994H3.09492V8.85504Z'
        fill='white'
      />
    </svg>
  ),
  telegram: (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M2.14747 11.8101C7.39413 9.5241 10.8931 8.01677 12.6445 7.2881C17.6435 5.2091 18.6815 4.8481 19.3585 4.8361C19.5085 4.8331 19.8405 4.8701 20.0565 5.0461C20.2385 5.1931 20.2885 5.3931 20.3125 5.5331C20.3365 5.6731 20.3665 5.9921 20.3425 6.2411C20.0725 9.0881 18.8995 15.9951 18.3025 19.1831C18.0505 20.5311 17.5545 20.9831 17.0725 21.0281C16.0275 21.1241 15.2345 20.3381 14.2225 19.6741C12.6375 18.6351 11.7425 17.9881 10.2045 16.9751C8.42747 15.8041 9.57947 15.1601 10.5925 14.1081C10.8575 13.8331 15.4625 9.6441 15.5525 9.2641C15.5625 9.2161 15.5735 9.0391 15.4685 8.9461C15.3635 8.8521 15.2085 8.8841 15.0955 8.9101C14.9361 8.9461 12.4055 10.6188 7.50347 13.9281C6.78413 14.4214 6.13347 14.6618 5.55147 14.6491C4.90847 14.6351 3.67147 14.2861 2.75347 13.9871C1.62547 13.6201 0.729468 13.4271 0.807468 12.8041C0.848135 12.4801 1.2948 12.1488 2.14747 11.8101Z'
        fill='white'
      />
    </svg>
  ),
};

const TECH_AND_PRODUCT_MEMBERS: TechAndProductMember[] = [
  {
    id: 1,
    name: 'Elijah Santos',
    role: 'Head of Product',
    image: members.tnp1,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/elijahsantos7/' },
      { type: 'telegram', url: 'https://t.me/zkeekei' },
    ],
  },
  {
    id: 2,
    name: 'Wesley Bellaïche',
    role: 'Product Manager',
    image: members.tnp2,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/wesley-ph0enix-4a4a043a5/' },
      { type: 'telegram', url: 'https://t.me/LFG_Ph0enix' },
    ],
  },
  {
    id: 3,
    name: 'Chris Nguyen',
    role: 'Chief Technology Officer',
    image: members.tnp3,
    socials: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/chris2502/' }],
  },
  {
    id: 4,
    name: 'Tin (Alex) Nguyen',
    role: 'Lead Engineer',
    image: members.tnp4,
    socials: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/tinguyen/' }],
  },
  // {
  //   id: 5,
  //   name: 'Rundo Bardinas',
  //   role: 'QA',
  //   image: members.mc8,
  //   socials: [
  // {
  //   type: 'linkedin',
  //   url: 'https://www.linkedin.com/in/rabi-rundo-bardinas-b373b7174/',
  // },
  //   ],
  // },
];

const SALES_BUSINESS_MEMBERS: TechAndProductMember[] = [
  {
    id: 1,
    name: 'Oliver Duhamel',
    role: 'Head of Sales',
    image: members.sbd1,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/oliver-duhamel-35110675/' },
      { type: 'telegram', url: 'https://t.me/MI_0xTheListener' },
    ],
  },
  {
    id: 2,
    name: 'Luca Mertens',
    role: 'Business Development',
    image: members.sbd3,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/lucamertens/' },
      { type: 'telegram', url: 'https://t.me/BDxLFG' },
    ],
  },
  {
    id: 3,
    name: 'Steven Tran',
    role: 'Business Development',
    image: members.sbd2,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/hung-tran-79485a343/' },
      { type: 'telegram', url: 'https://t.me/SteventheTee' },
    ],
  },
  {
    id: 4,
    name: 'Andrew Jang',
    role: 'Business Development',
    image: members.sbd4,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/bonghwan-jang-6546a9316/' },
      { type: 'telegram', url: 'https://t.me/NoGongju' },
    ],
  },
];

const MARKETING_COMMUNITY_MEMBERS: TechAndProductMember[] = [
  {
    id: 1,
    name: 'Dazzle Lee',
    role: 'Head of Marketing',
    image: members.mc1,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/dazzlelee/' },
      { type: 'telegram', url: 'https://t.me/Dazzle_dazzle' },
    ],
  },
  {
    id: 2,
    name: 'Anthony Mutuc',
    role: 'Growth Marketing',
    image: members.mc2,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/anthonymutuc/' },
      { type: 'telegram', url: 'https://t.me/a_lfg2026' },
    ],
  },
  {
    id: 3,
    name: 'Bryan Tam',
    role: 'Social Media Manager',
    image: members.mc3,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/bryan-tam-95681422b/' },
      { type: 'telegram', url: 'https://t.me/btamagucci' },
    ],
  },
  {
    id: 4,
    name: 'Jane Gulfan',
    role: 'Content and General Operational Support',
    image: members.mc4,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/janekohi/' },
      { type: 'telegram', url: 'https://t.me/koopakohi' },
    ],
  },
  {
    id: 5,
    name: 'Karl Mahusay',
    role: 'Community Engagement and Support',
    image: members.mc5,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/karl-mahusay-489572353/' },
      { type: 'telegram', url: 'https://t.me/ShieldHero6843' },
    ],
  },
  {
    id: 6,
    name: 'Alabi Mercy',
    role: 'Community Growth and Support',
    image: members.mc6,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/alabi-mercy/' },
      { type: 'telegram', url: 'https://t.me/Eniola_mercy' },
    ],
  },
  {
    id: 7,
    name: 'Jini Shin',
    role: 'Community Engagement and Support',
    image: members.mc7,
    socials: [
      { type: 'linkedin', url: 'https://www.linkedin.com/in/jaejin-shin-6b525525a/' },
      { type: 'telegram', url: 'https://t.me/shinjaejin' },
    ],
  },
  {
    id: 8,
    name: 'Rundo Bardinas',
    role: 'Community Engagement and Support',
    image: members.mc8,
    socials: [
      {
        type: 'linkedin',
        url: 'https://www.linkedin.com/in/rabi-rundo-bardinas-b373b7174/',
      },
    ],
  },
];

interface TeamCategorySectionProps {
  title: string;
  members: TechAndProductMember[];
}

const TeamCategorySection = ({ title, members }: TeamCategorySectionProps) => (
  <Stack gap={{ xs: 3, sm: 4 }} width={'min(1280px, 100%)'} zIndex={2}>
    <Typography
      component={'h3'}
      variant={'displayMedium'}
      className={'clip-text'}
      textAlign={'left'}
      whiteSpace={'pre-line'}
      fontSize={{ xs: '20px', sm: '36px' }}
      lineHeight={{ xs: '24px', sm: '48px' }}
      sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
      {title}
    </Typography>
    <Stack
      width={'100%'}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
        },
        columnGap: { xs: 2, sm: 3 },
        rowGap: { xs: 2, sm: 3 },
      }}>
      {members.map(member => (
        <Stack
          key={member.id}
          position={'relative'}
          height={{ xs: '208px', sm: '340px' }}
          width={'100%'}
          sx={{
            aspectRatio: '170.34 / 191.78',
            overflow: 'hidden',
            background: 'radial-gradient(133.4% 133.4% at 50% 0%, #111827 0%, #020617 100%)',
            backgroundImage: `url(${member.image})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}>
          <Stack
            position={'absolute'}
            bottom={{ xs: '18px', sm: '32px' }}
            left={{ xs: '18px', sm: '32px' }}
            gap={{ xs: 1, sm: 2 }}>
            <Stack>
              <Typography
                component={'p'}
                variant={'bodyLarge'}
                fontSize={{ xs: '14px', sm: '24px' }}
                lineHeight={{ xs: 'normal', sm: '32px' }}
                sx={{
                  color: '#FFFFFF',
                  textShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
                  fontWeight: 700,
                }}>
                {member.name}
              </Typography>
              <Typography
                component={'p'}
                variant={'bodyLarge'}
                fontSize={{ xs: '12px', sm: '18px' }}
                lineHeight={{ xs: '14px', sm: '24px' }}
                sx={{
                  color: '#FFFFFF',
                  opacity: 0.65,
                  mixBlendMode: 'plus-lighter',
                }}>
                {member.role}
              </Typography>
            </Stack>
            <Stack direction={'row'} gap={{ xs: 1.125, sm: 2 }}>
              {member.socials.map(social => (
                <Stack
                  key={social.type}
                  component={'a'}
                  href={social.url}
                  target={'_blank'}
                  rel='noreferrer'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 24, sm: 32 },
                    height: { xs: 24, sm: 32 },
                  }}>
                  {SOCIAL_ICON_MAP[social.type]}
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      ))}
    </Stack>
  </Stack>
);

export function TeamSection() {
  const [selectedMember, setSelectedMember] = useState<MemberItem>(MEMBERS[0] as MemberItem);

  return (
    <Stack
      width={'100%'}
      height={'100%'}
      flexGrow={1}
      alignItems={'center'}
      component={'section'}
      position={'relative'}
      bgcolor={'#0A0D16'}>
      <Stack
        bgcolor={'#060912'}
        className={'top-cut'}
        position={'absolute'}
        bottom={0}
        zIndex={1}
        left={'50%'}
        height={{ xs: '12px', sm: '30px' }}
        width={{ xs: 'min(160px, 100%)', sm: 'min(616px, 100%)' }}
        sx={{ transform: 'translateX(-50%)' }}
      />
      <Stack width={'100%'} padding={{ xs: '100px 24px 40px', sm: '80px' }} alignItems={'center'}>
        <Stack gap={{ xs: 3, sm: 7 }} width={'min(1280px, 100%)'} zIndex={2} alignItems={'center'}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            textAlign={{ xs: 'center', sm: 'left' }}
            alignSelf={{ xs: 'center', sm: 'flex-start' }}
            whiteSpace={'pre-line'}
            fontSize={{ xs: '24px', sm: '48px' }}
            lineHeight={{ xs: '32px', sm: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            Meet the team behind LFG Incorporated
          </Typography>
          <Typography
            component={'h3'}
            variant={'displayMedium'}
            className={'clip-text'}
            textAlign={'left'}
            alignSelf={'flex-start'}
            whiteSpace={'pre-line'}
            fontSize={{ xs: '20px', sm: '36px' }}
            lineHeight={{ xs: '24px', sm: '48px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            CO-FOUNDERS
          </Typography>
          <Stack
            width={'100%'}
            gap={5}
            alignItems={{ xs: 'center', sm: 'flex-start' }}
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent={'space-between'}>
            <Stack width={{ xs: '100%', sm: '70%' }}>
              <TeamCard name={selectedMember.name} image={selectedMember.image} decs={selectedMember.description} />
            </Stack>
            <Stack gap={2} width={{ xs: '100%', sm: '30%' }} direction={{ xs: 'row', sm: 'column' }} overflow={'auto'}>
              {MEMBERS.map(member => (
                <TeamCardList
                  key={member.id}
                  name={member.name}
                  image={member.image}
                  isSelected={member.id === selectedMember.id}
                  onClick={() => setSelectedMember(member)}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack width={'100%'} padding={{ xs: '0 24px 80px', sm: '0 80px 80px' }} alignItems={'center'}>
        <Stack gap={{ xs: 6, sm: 8 }} width={'100%'} alignItems={'center'}>
          <TeamCategorySection title='TECH AND PRODUCT' members={TECH_AND_PRODUCT_MEMBERS} />
          <TeamCategorySection title='SALES & BUSINESS DEVELOPMENT' members={SALES_BUSINESS_MEMBERS} />
          <TeamCategorySection title='MARKETING & COMMUNITY' members={MARKETING_COMMUNITY_MEMBERS} />
        </Stack>
      </Stack>
    </Stack>
  );
}
